import { I18n } from "aws-amplify/utils";
import { Box, Stack, Typography, Tooltip, Button } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { grey } from "@mui/material/colors";
import { useTemplateController } from "context";
import { GridFilterInputValue } from "@mui/x-data-grid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import NoteIcon from "@mui/icons-material/Note";
import InfoIcon from "@mui/icons-material/Info";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import DescriptionIcon from "@mui/icons-material/Description";
import AlarmOffIcon from "@mui/icons-material/AlarmOff";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import timezone from "dayjs/plugin/timezone";
import { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import countryTimezone from 'country-timezone';
import {
  AlarmOffOutlined,
  PauseCircleFilledOutlined,
} from "@mui/icons-material";
import { alignProperty } from "@mui/material/styles/cssUtils";
dayjs.extend(utc);
dayjs.extend(timezone);
const TypeDescriptionComponent = ({
  value,
  hasPrimaryNote,
  hasSecondaryNote,
  hasLastPlantNote,
  hasLastDeviceNote,
  hasLastProcessNote,
  primaryNote, // Aggiunto per mostrare il valore di primary_note
  secondaryNote, // Aggiunto per mostrare il valore di secondary_note
  lastPlantNote,
  lastPlantNoteDate,
  lastDeviceNote,
  lastDeviceNoteDate,
  lastDeviceType,
  lastProcessNote,
  lastProcessNoteDate,
}) => {
  const [controller] = useTemplateController();
  const { darkMode } = controller;

  // Define stateColor based on darkMode
  const stateColor = darkMode ? grey[200] : grey[800];
  const TooltipBig = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      fontSize: '1rem',
      maxWidth: "50rem",
      textAlign: "left",
    },
  }));

  return (
    <Box sx={{ display: "flex", alignItems: "center", marginTop: "0.3rem" }}>
      {hasPrimaryNote && primaryNote && (
        <TooltipBig title={primaryNote}>
          <NoteIcon
            sx={{ marginRight: 1, fontSize: "medium", color: stateColor }}
          />
        </TooltipBig>
      )}
      {hasSecondaryNote && secondaryNote && (
        <TooltipBig title={secondaryNote} >
          <InfoIcon
            sx={{ marginRight: 1, fontSize: "medium", color: stateColor}}
          />
        </TooltipBig>
      )}
      {hasLastPlantNote && lastPlantNote && lastPlantNoteDate && (
        <TooltipBig
          title={
            dayjs
              .utc(lastPlantNoteDate)
              .tz(dayjs.tz.guess())
              .format("YYYY-MM-DD HH:mm:ss") +
            " " +
            lastPlantNote
          }
        >
          <DescriptionIcon
            sx={{ marginRight: 1, fontSize: "medium", color: stateColor }}
          />
        </TooltipBig>
      )}
      {hasLastDeviceNote && lastDeviceNote && lastDeviceNoteDate && lastDeviceType && (
        <TooltipBig
          title={
            dayjs
              .utc(lastDeviceNoteDate)
              .tz(dayjs.tz.guess())
              .format("YYYY-MM-DD HH:mm:ss") +
            " " +
            lastDeviceType +
            " " +
            lastDeviceNote
          }
        >
          <SpeakerNotesIcon
            sx={{ marginRight: 1, fontSize: "medium", color: stateColor }}
          />
        </TooltipBig>
      )}
      {hasLastProcessNote && lastProcessNote && lastProcessNoteDate && (
        <TooltipBig
          title={
            dayjs
              .utc(lastProcessNoteDate)
              .tz(dayjs.tz.guess())
              .format("YYYY-MM-DD HH:mm:ss") +
            " " +
            lastProcessNote
          }
        >
          <WatchLaterIcon
            sx={{ marginRight: 1, fontSize: "medium", color: stateColor }}
          />
        </TooltipBig>
      )}
      <Typography style={{fontSize: "1rem"}}>
      {hasLastDeviceNote
        ? "| " : ""}
        {value}
      </Typography>
    </Box>
  );
};

const StateComponent = ({ value }) => {
  const [controller] = useTemplateController();
  const { darkMode } = controller;

  const stateColor = darkMode ? grey[200] : grey[800];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {value === "0" ? (
        <Stack direction="row">
          {"Inactive"}{" "}
          <CheckCircleIcon
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "1" ? (
        <Stack direction="row">
          {"Active"}{" "}
          <WarningIcon
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "2" ? (
        <Stack direction="row">
          {"Close"}{" "}
          <AlarmOffOutlined
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "3" ? (
        <Stack direction="row">
          {"Suspended"}{" "}
          <PauseCircleFilledOutlined
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "4" ? (
        <Stack direction="row">
          {"Suspended With Lock"}{" "}
          <PauseCircleOutlineIcon
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : (
        <Stack direction="row">
          {"Unknown"}{" "}
          <WarningIcon
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      )}
    </Box>
  );
};

export const datagridColumnsWithGrouping = (handleOpenMailPopup) => [
  {
    field: "original_store",
    headerName: I18n.get("Store Name"),
    width: 300,
    renderCell: (params) => {
      const {
        primary_note,
        secondary_note,
        last_plant_note,
        last_plant_note_date,
        last_process_note,
        last_process_note_date,
      } = params.row;
      const hasPrimaryNote = Boolean(primary_note);
      const hasSecondaryNote = Boolean(secondary_note);
      const hasLastPlantNote = Boolean(last_plant_note);
      const hasLastProcessNote = Boolean(last_process_note);

      return (
        <TypeDescriptionComponent
          value={params.value}
          hasPrimaryNote={hasPrimaryNote}
          hasSecondaryNote={hasSecondaryNote}
          primaryNote={primary_note} // Passa il valore di primary_note
          secondaryNote={secondary_note} // Passa il valore di secondary_note
          hasLastPlantNote={hasLastPlantNote}
          lastPlantNote={last_plant_note}
          lastPlantNoteDate={last_plant_note_date}
          hasLastProcessNote={hasLastProcessNote}
          lastProcessNote={last_process_note}
          lastProcessNoteDate={last_process_note_date}
        />
      );
    },
  },
  {
    field: "datetime_start",
    headerName: I18n.get("Start Date"),
    width: 180,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },
  {
    field: "local_datetime_start",
    headerName: I18n.get("Local Date"),
    width: 180,
    type: "dateTime",
    renderCell: (params) => {
      if (!params.row.datetime_start) {
        return ""; // Handle null or undefined values
      }
      try {
        const timezone = countryTimezone.getTimezones(params.row.billing_country)[0];
        if (timezone === undefined) {
          return "";
        }
        const formattedDate = dayjs.utc(params.row.datetime_start).tz(timezone).format("YYYY-MM-DD HH:mm:ss");
        return formattedDate;
      }catch (error) {
        console.error("Error formatting date:", error);
        return "";
      }
    },
  },
  { field: "last_modifier", headerName: I18n.get("Last Modifier"), width: 100 },

  {
    field: "original_equipment",
    headerName: I18n.get("Original Equipment"),
    width: 400,
    renderCell: (params) => {
      const {
        last_device_note,
        last_device_note_date,
        alarm_type_description,
      } = params.row;
      const hasLastDeviceNote = Boolean(last_device_note);

      return (
        <TypeDescriptionComponent
          value={params.value}
          hasLastDeviceNote={hasLastDeviceNote}
          lastDeviceNote={last_device_note}
          lastDeviceNoteDate={last_device_note_date}
          lastDeviceType={alarm_type_description}
        />
      );
    },
  },
  // {
  //   field: "service_territory_name",
  //   headerName: I18n.get("Service Territory"),
  //   width: 400,
  // },
  { field: "position", headerName: I18n.get("Position"), width: 300 },
  {
    field: "store_management",
    headerName: I18n.get("Store Management"),
    width: 200,
  },
  { field: "shift", headerName: I18n.get("Shift"), width: 80 },
  {
    field: "alarm_text",
    headerName: I18n.get("Alarm Text"),
    width: 200,
  },

  {
    field: "state",
    headerName: I18n.get("State"),
    width: 80,
    renderCell: (params) => {
      if (params.rowNode?.type === "group") {
        return undefined;
      }
      return <StateComponent value={params.value} />;
    },
    filterOperators: [
      {
        label: "Equals",
        value: "equals",
        getApplyFilterFn: (filterItem, column) => {
          if (!filterItem.field || !filterItem.value || !filterItem.operator) {
            return null;
          }
          const filterValue = filterItem.value.toLowerCase();
          return (value, row, column, apiRef) => {
            if (filterValue === "inactive") {
              return value === "0";
            } else if (filterValue === "active") {
              return value === "1";
            } else if (filterValue === "closed") {
              return value === "2";
            } else if (filterValue === "suspended") {
              return value === "3";
            } else if (filterValue === "suspended with lock") {
              return value === "4";
            }
            return false;
          };
        },
        InputComponent: GridFilterInputValue,
      },
    ],
  },

  {
    field: "alarm_note",
    headerName: I18n.get("Remark"),
    width: 200,
  },
  {
    field: "phone",
    headerName: I18n.get("Phone"),
    width: 60,
    renderCell: (params) => (
      <Typography
        component="a"
        href={`tel:${params.value}`}
        sx={{
          fontSize: "medium",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {params.value}
      </Typography>
    ),
  },
  { field: "system_id", headerName: I18n.get("System ID"), width: 80 },
  {
    field: "original_priority",
    headerName: I18n.get("Priority"),
    width: 50,
  },
  {
    field: "datetime_end",
    headerName: I18n.get("End Date"),
    width: 200,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },

  {
    field: "source_technology",
    headerName: I18n.get("Source Technology"),
    width: 200,
  },

  {
    field: "alarm_type_description",
    headerName: I18n.get("Alarm Type Description"),
    width: 300,
  },
  {
    field: "severity",
    headerName: I18n.get("Severity"),
    width: 80,
  },
  {
    field: "suspension_end_datetime",
    headerName: I18n.get("Suspension End Date"),
    width: 300,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },
  {
    field: "retailer",
    headerName: I18n.get("Retailer"),
    width: 200,
  },
  {
    field: "last_modification_type",
    headerName: I18n.get("Last Modification Type"),
    width: 200,
  },
  {
    field: "last_modification_date",
    headerName: I18n.get("Last Modification Date"),
    width: 200,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },
  {
    field: "datetime_edp_processing",
    headerName: I18n.get("Arrival Date"),
    width: 200,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },
  {
    field: "agent_in_charge",
    headerName: I18n.get("Agent in Charge"),
    width: 300,
    renderCell: (params) => (
      <Typography
        component="a"
        href={`mailto:${params.value}`}
        sx={{
          color: "white", // Colore blu scuro
          fontSize: "medium",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {params.value}
      </Typography>
    ),
  },

  {
    field: "name",
    headerName: I18n.get("Account Name"),
    width: 400,
  },
  {
    field: "original_message",
    headerName: I18n.get("Original Message"),
    width: 200,
    // renderCell: (params) =>
    //   params.rowNode?.type !== "group" && (
    //     <Typography
    //       onClick={() => handleOpenMailPopup(params.value)}
    //       sx={{
    //         fontSize: "medium",
    //         cursor: "pointer",
    //         textDecoration: "underline",
    //         fontWeight: "bold",
    //       }}
    //     >
    //       {I18n.get("Open message")}
    //     </Typography>
    //   ),
  },

  {
    field: "id",
    headerName: I18n.get("ID"),
    width: 200,
  },
  { field: "agent_closed", headerName: I18n.get("Agent Closed"), width: 250 },

  {
    field: "datetime_close",
    headerName: I18n.get("Close Date"),
    width: 200,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },

  {
    field: "case_number",
    headerName: I18n.get("Case Number"),
    width: 130,
    renderCell: (params) => (
      <Typography
        component="a"
        href={
          process.env.REACT_APP_ENV_ENVIRONMENT != "prod"
            ? `https://eptarefrigeration--bugfix.sandbox.lightning.force.com/${params.row.case_id}`
            : `https://eptarefrigeration.lightning.force.com/${params.row.case_id}`
        }
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          fontSize: "medium",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {params.value}
      </Typography>
    ),
  },

  {
    field: "account_number",
    headerName: I18n.get("Account Number"),
    width: 100,
  },
  {
    field: "service_territory_name",
    headerName: I18n.get("Territory Name"),
    width: 200,
  },
  {
    field: "duration",
    headerName: I18n.get("Duration"),
    width: 150,
    renderCell: (params) => {
      //If duration starts with 0 days, extract the time part
      if(params.value !== null && params.value !== undefined){
        let timePart = params.value;
        if (params.value.startsWith("0 days")) {
          timePart = timePart.split(",")[1]?.trim();
          //return timePart ? timePart : null;
        }
        if (params.value.includes("00h")){
          timePart = timePart.replace("00h:", "");
        }
        if (params.value.includes("-")){
          timePart = timePart.replaceAll("-", "");
          timePart = "-" + timePart;
        }
        
        return timePart ? timePart : null;
      }
      return params.value;
    },
        sortComparator: (v1, v2) => {
      const getNumber = (val) => {
        if (val === null || val === undefined) {
          return 0;
        } else {
          let days = val.split("days")[0].trim();
          let time = val.split("days")[1].replace(/[h,']/g, "").trim();

          // Convert days and time to total minutes in order to compare
          let date = days * 24 * 60 + time.split(":")[0] * 60 + time.split(":")[1] * 1;
          return date;
        }
      };
      return getNumber(v1) - getNumber(v2);
    },
  },

  {
    field: "serial_number",
    headerName: I18n.get("Serial Number"),
    width: 200,
  },
  {field:"process_note",
    headerName:I18n.get("Process Note"),
    width:200
  },
  {
    field: "email",
    headerName: I18n.get("Email"),
    width: 200,
    renderCell: (params) => (
      <Typography
        component="a"
        href={`mailto:${params.value}`}
        sx={{
          fontSize: "medium",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: "billing_country",
    headerName: I18n.get("Country"),
    width: 200,
  },
  {
    field: "billing_city",
    headerName: I18n.get("City"),
    width: 200,
  },
  {
    field: "billing_street",
    headerName: I18n.get("Street"),
    width: 200,
  },
  {
    field: "billing_postal_code",
    headerName: I18n.get("Postal Code"),
    width: 200,
  },
];

export const datagridColumnsWithNoGrouping = () => [
  {
    field: "original_store",
    headerName: I18n.get("Store Name"),
    width: 300,
    renderCell: (params) => {
      const {
        primary_note,
        secondary_note,
        last_plant_note,
        last_plant_note_date,
        last_process_note,
        last_process_note_date,
      } = params.row;
      const hasPrimaryNote = Boolean(primary_note);
      const hasSecondaryNote = Boolean(secondary_note);
      const hasLastPlantNote = Boolean(last_plant_note);
      const hasLastProcessNote = Boolean(last_process_note);

      return (
        <TypeDescriptionComponent
          value={params.value}
          hasPrimaryNote={hasPrimaryNote}
          hasSecondaryNote={hasSecondaryNote}
          primaryNote={primary_note} // Passa il valore di primary_note
          secondaryNote={secondary_note} // Passa il valore di secondary_note
          hasLastPlantNote={hasLastPlantNote}
          lastPlantNote={last_plant_note}
          lastPlantNoteDate={last_plant_note_date}
          hasLastProcessNote={hasLastProcessNote}
          lastProcessNote={last_process_note}
          lastProcessNoteDate={last_process_note_date}
        />
      );
    },
  },
  {
    field: "datetime_start",
    headerName: I18n.get("Start Date"),
    width: 200,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },
  {
    field: "local_datetime_start",
    headerName: I18n.get("Local Date"),
    width: 180,
    type: "dateTime",
    renderCell: (params) => {
      if (!params.row.datetime_start) {
        return ""; // Handle null or undefined values
      }

      try {
        const timezone = countryTimezone.getTimezones(params.row.billing_country)[0];
        if(timezone === undefined){
          return ""; // Handle undefined timezone
        }
        const formattedDate = dayjs.utc(params.row.datetime_start).tz(timezone).format("YYYY-MM-DD HH:mm:ss");
        return formattedDate;
      }catch (error) {
        console.error("Error formatting date:", error);
        return "";
      }
    },
  },
  { field: "last_modifier", headerName: I18n.get("Last Modifier"), width: 100 },

  {
    field: "original_equipment",
    headerName: I18n.get("Original Equipment"),
    width: 400,
    renderCell: (params) => {
      const {
        last_device_note,
        last_device_note_date,
        alarm_type_description,
      } = params.row;
      const hasLastDeviceNote = Boolean(last_device_note);

      return (
        <TypeDescriptionComponent
          value={params.value}
          hasLastDeviceNote={hasLastDeviceNote}
          lastDeviceNote={last_device_note}
          lastDeviceNoteDate={last_device_note_date}
          lastDeviceType={alarm_type_description}
        />
      );
    },
  },
  { field: "position", headerName: I18n.get("Position"), width: 300 },
  {
    field: "store_management",
    headerName: I18n.get("Store Management"),
    width: 200,
  },
  { field: "shift", headerName: I18n.get("Shift"), width: 80 },
  {
    field: "alarm_text",
    headerName: I18n.get("Alarm Text"),
    width: 200,
  },

  {
    field: "state",
    headerName: I18n.get("State"),
    width: 80,
    renderCell: (params) => {
      if (params.rowNode?.type === "group") {
        return undefined;
      }
      return <StateComponent value={params.value} />;
    },
    filterOperators: [
      {
        label: "Equals",
        value: "equals",
        getApplyFilterFn: (filterItem, column) => {
          if (!filterItem.field || !filterItem.value || !filterItem.operator) {
            return null;
          }
          const filterValue = filterItem.value.toLowerCase();
          return (value, row, column, apiRef) => {
            if (filterValue === "inactive") {
              return value === "0";
            } else if (filterValue === "active") {
              return value === "1";
            } else if (filterValue === "closed") {
              return value === "2";
            } else if (filterValue === "suspended") {
              return value === "3";
            } else if (filterValue === "suspended with lock") {
              return value === "4";
            }
            return false;
          };
        },
        InputComponent: GridFilterInputValue,
      },
    ],
  },

  {
    field: "alarm_note",
    headerName: I18n.get("Remark"),
    width: 200,
  },
  {
    field: "phone",
    headerName: I18n.get("Phone"),
    width: 60,
    renderCell: (params) => (
      <Typography
        component="a"
        href={`tel:${params.value}`}
        sx={{
          fontSize: "medium",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {params.value}
      </Typography>
    ),
  },
  { field: "system_id", headerName: I18n.get("System ID"), width: 80 },
  {
    field: "original_priority",
    headerName: I18n.get("Priority"),
    width: 50,
  },
  {
    field: "datetime_end",
    headerName: I18n.get("End Date"),
    width: 200,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },

  {
    field: "source_technology",
    headerName: I18n.get("Source Technology"),
    width: 200,
  },

  {
    field: "alarm_type_description",
    headerName: I18n.get("Alarm Type Description"),
    width: 300,
  },
  {
    field: "severity",
    headerName: I18n.get("Severity"),
    width: 200,
  },
  {
    field: "suspension_end_datetime",
    headerName: I18n.get("Suspension End Date"),
    width: 300,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },
  {
    field: "retailer",
    headerName: I18n.get("Retailer"),
    width: 200,
  },
  {
    field: "last_modification_type",
    headerName: I18n.get("Last Modification Type"),
    width: 200,
  },
  {
    field: "last_modification_date",
    headerName: I18n.get("Last Modification Date"),
    width: 200,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },
  {
    field: "datetime_edp_processing",
    headerName: I18n.get("Arrival Date"),
    width: 200,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },
  {
    field: "agent_in_charge",
    headerName: I18n.get("Agent in Charge"),
    width: 300,
    renderCell: (params) => (
      <Typography
        component="a"
        href={`mailto:${params.value}`}
        sx={{
          color: "white", // Colore blu scuro
          fontSize: "medium",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {params.value}
      </Typography>
    ),
  },

  {
    field: "name",
    headerName: I18n.get("Account Name"),
    width: 400,
  },
  {
    field: "account_number",
    headerName: I18n.get("Account Number"),
    width: 100,
  },
  {
    field: "service_territory_name",
    headerName: I18n.get("Territory Name"),
    width: 200,
  },
  {
    field: "original_message",
    headerName: I18n.get("Original Message"),
    width: 200,
  },

  {
    field: "id",
    headerName: I18n.get("ID"),
    width: 200,
  },
  { field: "agent_closed", headerName: I18n.get("Agent Closed"), width: 300 },

  {
    field: "datetime_close",
    headerName: I18n.get("Close Date"),
    width: 200,
    type: "dateTime",
    valueFormatter: (value) => {
      if (!value) {
        return ""; // Handle null or undefined values
      }
      const formattedDate = dayjs
        .utc(value)
        .tz(dayjs.tz.guess())
        .format("YYYY-MM-DD HH:mm:ss");
      return formattedDate;
    },
  },

  {
    field: "case_number",
    headerName: I18n.get("Case Number"),
    width: 200,
    renderCell: (params) => (
      <Typography
        component="a"
        href={
          process.env.REACT_APP_ENV_ENVIRONMENT != "prod"
            ? `https://eptarefrigeration--bugfix.sandbox.lightning.force.com/${params.row.case_id}`
            : `https://eptarefrigeration.lightning.force.com/${params.row.case_id}`
        }
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          fontSize: "medium",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {params.value}
      </Typography>
    ),
  },

  {
    field: "duration",
    headerName: I18n.get("Duration"),
    width: 150,
    renderCell: (params) => {
      //If duration starts with 0 days, extract the time part
      if(params.value !== null && params.value !== undefined){
        let timePart = params.value;
        if (params.value.startsWith("0 days")) {
          timePart = timePart.split(",")[1]?.trim();
          //return timePart ? timePart : null;
        }
        if (params.value.includes("00h")){
          timePart = timePart.replace("00h:", "");
        }
        if (params.value.includes("-")){
          timePart = timePart.replaceAll("-", "");
          timePart = "-" + timePart;
        }
        return timePart ? timePart : null;
      }
      return params.value;
    },
    sortComparator: (v1, v2) => {
      const getNumber = (val) => {
        if (val === null || val === undefined) {
          return 0;
        } else {
          let days = val.split("days")[0].trim();
          let time = val.split("days")[1].replace(/[h,']/g, "").trim();
          
          // Convert days and time to total minutes in order to compare
          let date = days * 24 * 60 + time.split(":")[0] * 60 + time.split(":")[1] * 1;
          return date;
        }
      };
      return getNumber(v1) - getNumber(v2);
    },
  },

  {
    field: "serial_number",
    headerName: I18n.get("Serial Number"),
    width: 200,
  },
  {field:"process_note",
    headerName:I18n.get("Process Note"),
    width:200
  },
  {
    field: "email",
    headerName: I18n.get("Email"),
    width: 200,
    renderCell: (params) => (
      <Typography
        component="a"
        href={`mailto:${params.value}`}
        sx={{
          fontSize: "medium",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: "billing_country",
    headerName: I18n.get("Country"),
    width: 200,
  },
  {
    field: "billing_city",
    headerName: I18n.get("City"),
    width: 200,
  },
  {
    field: "billing_street",
    headerName: I18n.get("Street"),
    width: 200,
  },
  {
    field: "billing_postal_code",
    headerName: I18n.get("Postal Code"),
    width: 200,
  },
];
